import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowUpRight, Play, Check } from "lucide-react";
import { BRAND, TRUST_BADGES } from "../../data/content";
import { MaskedLines } from "../Reveal";
import LiveTicker from "../LiveTicker";

const EASE = [0.16, 1, 0.3, 1];
const PARTICLES = Array.from({ length: 26 });

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const rotX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const floatX = useTransform(sx, [-0.5, 0.5], [-24, 24]);
  const floatY = useTransform(sy, [-0.5, 0.5], [-16, 16]);

  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -80, duration: 1.6 });
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouse}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center pt-28 pb-16"
    >
      {/* Ambient */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
        <div className="ambient-emerald" style={{ width: 620, height: 620, top: "-8%", left: "-6%" }} />
        <div className="ambient-gold" style={{ width: 480, height: 480, bottom: "0%", right: "4%" }} />
      </motion.div>

      {/* Golden glowing lines */}
      <div className="absolute inset-0 z-0 opacity-60">
        <div className="absolute top-1/4 left-0 w-full hairline-gold opacity-30" />
        <div className="absolute top-2/3 left-0 w-full hairline-gold opacity-20" />
        <div className="absolute left-1/2 top-0 h-full w-px bg-gradient-to-b from-transparent via-gk-gold/20 to-transparent" />
      </div>

      {/* Particles */}
      <div className="absolute inset-0 z-0">
        {PARTICLES.map((_, i) => {
          const size = Math.random() * 3 + 1;
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const dur = Math.random() * 10 + 8;
          return (
            <motion.span
              key={i}
              className="absolute rounded-full bg-gk-champagne"
              style={{ width: size, height: size, left: `${left}%`, top: `${top}%`, opacity: 0.25 }}
              animate={{ y: [0, -30, 0], opacity: [0.1, 0.5, 0.1] }}
              transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay: Math.random() * 5 }}
            />
          );
        })}
      </div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center w-full">
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center gap-3 glass rounded-full px-4 py-2 mb-6"
          >
            <span className="h-2 w-2 rounded-full bg-gk-gold animate-pulse" />
            <span className="font-inter text-[11px] tracking-[0.3em] uppercase text-white/70">Premium Digital Branding Agency</span>
          </motion.div>

          <h1 className="font-cinzel font-semibold leading-[1.05] text-white">
            <MaskedLines
              lines={["Build Brands That", "Inspire Trust"]}
              delay={0.4}
              className="block text-2xl sm:text-4xl lg:text-5xl text-white"
              lineClass="py-0.5"
            />
            <MaskedLines
              lines={["& Growth."]}
              delay={0.7}
              className="block text-2xl sm:text-4xl lg:text-5xl text-gold-metallic"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="font-poppins text-xs md:text-sm text-white/55 leading-relaxed mt-6 max-w-xl"
          >
            GK Brandify helps startups and businesses build premium brands, modern websites and powerful digital experiences that attract customers and increase credibility.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-8"
          >
            <button
              data-testid="hero-cta-project"
              onClick={() => goTo("contact")}
              className="btn-gold font-inter text-sm font-medium px-6 py-3 rounded-full inline-flex items-center gap-2 group"
            >
              Start Your Project
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-portfolio"
              onClick={() => goTo("portfolio")}
              className="btn-glass font-inter text-sm font-medium px-6 py-3 rounded-full inline-flex items-center gap-2"
            >
              <Play size={14} className="fill-current" />
              View Portfolio
            </button>
          </motion.div>

          <div className="mt-8">
            <LiveTicker />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8, ease: EASE }}
            className="mt-6 flex flex-wrap gap-x-5 gap-y-2"
          >
            {TRUST_BADGES.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 font-inter text-[11px] md:text-xs text-white/55">
                <Check size={13} className="text-gk-gold" strokeWidth={3} />
                {b}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Right — floating glass devices */}
        <div className="lg:col-span-5 relative h-[360px] md:h-[440px] hidden md:block" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateX: rotX, rotateY: rotY, x: floatX, y: floatY, transformStyle: "preserve-3d" }}
            className="relative w-full h-full"
          >
            {/* MacBook glass card */}
            <motion.div
              animate={{ y: [0, -18, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-6 left-0 w-[86%] rounded-2xl overflow-hidden glass-strong shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
              style={{ transform: "translateZ(40px)" }}
            >
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                <span className="h-2.5 w-2.5 rounded-full bg-gk-gold/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
              </div>
              <img
                src="https://images.unsplash.com/photo-1595683363301-1e94594a550d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Luxury website preview"
                loading="lazy"
                className="w-full h-56 md:h-64 object-cover"
              />
              <div className="p-4">
                <div className="h-2 w-24 rounded-full bg-gk-gold/50 mb-2" />
                <div className="h-2 w-40 rounded-full bg-white/10" />
              </div>
            </motion.div>

            {/* Phone glass card */}
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute bottom-0 right-0 w-32 md:w-40 rounded-3xl overflow-hidden glass-strong border-gk-gold/20 shadow-[0_30px_70px_rgba(0,0,0,0.7)]"
              style={{ transform: "translateZ(90px)" }}
            >
              <img
                src="https://images.unsplash.com/photo-1695048132853-026f93f40f7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Luxury mobile app preview"
                loading="lazy"
                className="w-full h-52 md:h-64 object-cover"
              />
            </motion.div>

            {/* Floating logo chip */}
            <motion.div
              animate={{ y: [0, -12, 0], rotate: [0, 3, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 right-4 w-16 h-16 rounded-2xl glass flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
              style={{ transform: "translateZ(130px)" }}
            >
              <img src={BRAND.logo} alt="logo" className="w-11 h-11 object-cover rounded-xl" />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-inter text-[10px] tracking-[0.3em] uppercase text-white/40">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-gk-gold to-transparent" />
      </motion.div>
    </section>
  );
}
